import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Field, Formik } from "formik";
import { Layout, Loader } from "src/components";
import useSkinPath from "src/hooks/useSkinPath";
import useSkin from "src/hooks/useSkin";
import arrowDownIcon from "src/assets/images/down-arrow.svg";
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Area, AreaChart } from "recharts";
import {
  StyledCurrentFilterDescription,
  StyledDatePicker,
  StyledDatePickerInputGroup,
  StyledDateRangeWrapper,
  StyledReportFilter,
  StyledTabButton,
  StyledTabsContainer,
  StyledExportContainer
} from "./GroupReports.styles";

import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import enGB from "date-fns/locale/en-GB";
import { RootReducer } from "src/redux/reducers";
import { useDispatch, useSelector } from "react-redux";
import { getClassDetailsByDateActions, getClassDetailsPerUserActions } from "src/redux/actions/reports";
import { ClassDetailsUserTypes } from "src/redux/actions/reports/types";
import useSchools from "src/hooks/useSchools";
import { debounce } from "lodash";
import * as XLSX from "xlsx";

/** Read a CSS custom-property value at runtime (needed for JS-based chart configs). */
const cssVar = (prop: string): string =>
  getComputedStyle(document.querySelector(".griffle-page") ?? document.documentElement)
    .getPropertyValue(prop)
    .trim();

registerLocale("en-GB", enGB);

const reportTypes = [
  { label: "Number of Books Read", value: 1 },
  { label: "Minutes Read", value: 2 },
  { label: "Number of Quizzes Completed", value: 3 }
];

const GroupReports = () => {
  const { activeSchoolId, getGroups } = useSchools();
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const [selectedClass, setSelectedClass] = useState(-999);
  const { path } = useSkinPath();
  const { isGriffle } = useSkin();
  const [selectedReportType, setSelectedReportType] = useState(1);
  const [chartView, setChartView] = useState("Month");

  const {
    group: { groups },
    reports: { classDetails, classDetailsByDate }
  }: RootReducer = useSelector((state: RootReducer) => state);

  const [isLoading, setIsLoading] = useState(true);

  const [fromDate, setFromDate] = useState<Date | null>(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date;
  });
  const [toDate, setToDate] = useState<Date | null>(() => new Date());

  const [tableData, setTableData] = useState<ClassDetailsUserTypes[]>([]);
  const [groupsData, setGroupsData] = useState<{ id: number; label: string }[]>([]);
  const [chartData, setChartData] = useState<{ date: string; value: number }[]>([]);

  const columnHelper = createColumnHelper<ClassDetailsUserTypes[][0]>();
  const reportValue = useCallback(() => {
    return reportTypes.find((type) => type.value === selectedReportType)?.label ?? "";
  }, [selectedReportType]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("rank", { header: "Rank", cell: (info) => info.getValue() }),
      columnHelper.accessor("name", { header: "Name", cell: (info) => info.getValue() }),
      columnHelper.accessor("value", { header: reportValue(), cell: (info) => info.getValue() })
    ],
    [reportValue]
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  const getClassReport = useCallback(
    (id: number, reportType: number, startDate: Date, endDate: Date, selectedClass: number) => {
      dispatch(
        getClassDetailsPerUserActions.request({
          id,
          groupId: selectedClass === -999 ? undefined : selectedClass,
          reportType,
          startDate: startOfDay(startDate).toISOString(),
          endDate: endOfDay(endDate).toISOString()
        })
      );
    },
    [dispatch]
  );

  const getChartReport = useCallback(
    (id: number, reportType: number, startDate: Date, endDate: Date, chartType: number, selectedClass: number) => {
      dispatch(
        getClassDetailsByDateActions.request({
          id,
          groupId: selectedClass === -999 ? undefined : selectedClass,
          reportType,
          startDate: startOfDay(startDate).toISOString(),
          endDate: endOfDay(endDate).toISOString(),
          groupBy: chartType
        })
      );
    },
    [dispatch]
  );

  const debouncedGetClassReport = useMemo(
    () => debounce((...args: Parameters<typeof getClassReport>) => getClassReport(...args), 300),
    [getClassReport]
  );
  const debouncedGetChartReport = useMemo(
    () => debounce((...args: Parameters<typeof getChartReport>) => getChartReport(...args), 300),
    [getChartReport]
  );

  const startOfDay = (date: Date) => {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
  };

  const endOfDay = (date: Date) => {
    const newDate = new Date(date);
    newDate.setHours(23, 59, 59, 0);
    return newDate;
  };

  const exportToExcel = () => {
    if (!tableData || tableData.length === 0) {
      alert("No table data to export.");
      return;
    }

    // Create a new workbook
    const workbook = XLSX.utils.book_new();

    // Prepare the data for export with appropriate headers
    const worksheetData = [
      // Headers
      ["Rank", "Name", reportValue()] // Dynamic report value label for the third column
    ];

    // Add each row of data from tableData
    tableData.forEach((item) => {
      worksheetData.push([item.rank.toString(), item.name, item.value.toString()]);
    });

    // Convert the array to a worksheet and append it to the workbook
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Progress Report");

    // Export the workbook as an Excel file
    XLSX.writeFile(workbook, `progress_report_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  useEffect(() => {
    if (groups) {
      const allGroup = { id: -999, label: "All classes" };
      setGroupsData([allGroup, ...groups.rows.map((group) => ({ id: group.id, label: group.name }))]);
    }
  }, [groups]);

  useEffect(() => {
    if (selectedReportType && fromDate && toDate && activeSchoolId && chartView && selectedClass) {
      getGroups();
      debouncedGetClassReport(activeSchoolId, selectedReportType, fromDate, toDate, selectedClass);
      debouncedGetChartReport(
        activeSchoolId,
        selectedReportType,
        fromDate,
        toDate,
        chartView === "Month" ? 1 : 2,
        selectedClass
      );
    }
  }, [selectedReportType, fromDate, toDate, activeSchoolId, selectedClass, chartView]);

  useEffect(() => {
    if (classDetails) {
      setTableData(classDetails);
    }
    setChartData(classDetailsByDate?.map((item) => ({ date: item.date, value: item.value })) || []);
    setIsLoading(false);
  }, [classDetails, classDetailsByDate]);

  if (isLoading) return <Loader />;

  return (
    <Layout>
      <div className="assignments-page">
        {/* Breadcrumb */}
        <div className="main_breadcrumb">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">{t("reports_breadcrumb_progress_report")}</li>
              <li className="breadcrumb-item active" aria-current="page">
                <Link to={path("/progress-report-detailed")}>{t("sidebar_link_progress_detailed")}</Link>
              </li>
            </ol>
          </nav>
        </div>

        <StyledReportFilter $isGriffle={isGriffle}>
          {/* Dropdown for Class Selection */}
          <Formik
            initialValues={{ selectedClass }}
            onSubmit={() => {
              console.log("submit");
            }}
          >
            <div className="form-group mb0">
              <div className="relative">
                <Field
                  as="select"
                  name="selectedClass"
                  className="form-control"
                  value={selectedClass}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedClass(Number(e.target.value))}
                >
                  {groupsData.map(({ id, label }) => (
                    <option value={id} key={id}>
                      {label}
                    </option>
                  ))}
                </Field>
                <span className="down-img">
                  <img src={arrowDownIcon} alt="icon" />
                </span>
              </div>
            </div>
          </Formik>

          <StyledDateRangeWrapper>
            <StyledDatePickerInputGroup $isGriffle={isGriffle}>
              <label htmlFor="from-date">{t("From")}</label>
              <StyledDatePicker
                $isGriffle={isGriffle}
                id="from-date"
                locale="en-GB"
                selected={fromDate}
                onChange={(date) => {
                  if (date instanceof Date || date === null) {
                    setFromDate(date);
                  } else if (Array.isArray(date)) {
                    setFromDate(date[0]);
                  }
                }}
                dateFormat="dd/MM/yyyy"
                placeholderText={t("Select a date") as string}
              />
            </StyledDatePickerInputGroup>
            <StyledDatePickerInputGroup $isGriffle={isGriffle}>
              <label htmlFor="to-date">{t("To")}</label>
              <StyledDatePicker
                $isGriffle={isGriffle}
                id="to-date"
                locale="en-GB"
                selected={toDate}
                onChange={(date) => {
                  if (date instanceof Date || date === null) {
                    setToDate(date);
                  } else if (Array.isArray(date)) {
                    setToDate(date[1]);
                  }
                }}
                dateFormat="dd/MM/yyyy"
                placeholderText={t("Select a date") as string}
              />
            </StyledDatePickerInputGroup>
          </StyledDateRangeWrapper>
        </StyledReportFilter>

        {/* Report Type Tabs */}
        <StyledTabsContainer $isGriffle={isGriffle}>
          {reportTypes.map((type) => (
            <StyledTabButton
              $isGriffle={isGriffle}
              key={type.value}
              active={selectedReportType === type.value}
              onClick={() => setSelectedReportType(type.value)}
            >
              {type.label}
            </StyledTabButton>
          ))}
        </StyledTabsContainer>

        <StyledExportContainer>
          <button type="button" className="btn btn-class" onClick={exportToExcel}>
            {t("assignments_button_export")}
          </button>
        </StyledExportContainer>

        {/* Table */}
        <StyledCurrentFilterDescription $isGriffle={isGriffle}>
          <p>
            <b>{groupsData.find((classItem) => classItem.id === selectedClass)?.label}</b> (
            {fromDate?.toLocaleDateString("en-GB")} - {toDate?.toLocaleDateString("en-GB")})
          </p>
        </StyledCurrentFilterDescription>

        <div className="assignmentsTableWrapper ds-card sec_body">
          <div className="report-table-container">
            <table className="report-table">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className={row.index % 2 === 0 ? "even" : "odd"}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 20, paddingRight: 10 }}>
            <Link to={path("/progress-report-more-details")} style={{ textDecoration: "underline" }}>
              {t("More details")}
            </Link>
          </div>
        </div>

        {/* Line Chart Section */}
        <div
          className="tab-pane fade active show"
          id="progress-tab-pane"
          role="tabpanel"
          aria-labelledby="progress-tab"
        >
          <div className="ds-card">
            <div className="d-sm-flex align-items-center justify-content-between pb-3">
              <h6 className="pro_text">{t("student_profile_button_label_progress_reports")}</h6>
            </div>
            <div className="ds-card sec_body">
              {chartData?.length ? (
                <div className="report-chart-wrapper">
                  <div className="report-chart-toggle-buttons">
                    <button
                      className={`toggle-button ${chartView === "Month" ? "active" : ""}`}
                      onClick={() => setChartView("Month")}
                    >
                      Month
                    </button>
                    <button
                      className={`toggle-button ${chartView === "Week" ? "active" : ""}`}
                      onClick={() => setChartView("Week")}
                    >
                      Week
                    </button>
                  </div>

                  <div
                    className="report-chart-container"
                    style={
                      isGriffle ? { backgroundColor: "var(--color-surface)", borderRadius: 10, padding: 16 } : undefined
                    }
                  >
                    <ResponsiveContainer width="100%" height={400}>
                      {isGriffle ? (
                        <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 50 }}>
                          <defs>
                            <linearGradient id="griffleGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={cssVar("--color-primary")} stopOpacity={0.2} />
                              <stop offset="95%" stopColor={cssVar("--color-primary")} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid vertical={false} stroke={cssVar("--color-border")} strokeDasharray="none" />
                          <XAxis
                            dataKey="date"
                            tickFormatter={(tick) => tick}
                            tick={{
                              fontSize: 12,
                              fill: cssVar("--color-ink-muted"),
                              fontFamily: cssVar("--font-body")
                            }}
                            axisLine={{ stroke: cssVar("--color-border") }}
                            tickLine={false}
                            angle={-45}
                            textAnchor="end"
                          />
                          <YAxis
                            tick={{
                              fontSize: 12,
                              fill: cssVar("--color-ink-muted"),
                              fontFamily: cssVar("--font-body")
                            }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <Tooltip
                            labelFormatter={(label) => label}
                            contentStyle={{
                              borderRadius: 8,
                              border: `1px solid ${cssVar("--color-border")}`,
                              fontFamily: cssVar("--font-body"),
                              fontSize: 13,
                              boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
                            }}
                            labelStyle={{ fontWeight: 600, color: cssVar("--color-ink") }}
                            itemStyle={{ color: cssVar("--color-primary") }}
                          />
                          <Area
                            type="monotone"
                            dataKey="value"
                            stroke={cssVar("--color-primary")}
                            strokeWidth={2.5}
                            fill="url(#griffleGradient)"
                            dot={{ r: 4, fill: cssVar("--color-primary"), stroke: "#fff", strokeWidth: 2 }}
                            activeDot={{ r: 6, fill: cssVar("--color-primary-dark"), stroke: "#fff", strokeWidth: 2 }}
                          />
                        </AreaChart>
                      ) : (
                        <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 50 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="date"
                            tickFormatter={(tick) => tick}
                            tick={{ fontSize: 12, fill: "#666" }}
                            axisLine={{ stroke: "#ccc" }}
                            tickLine={{ stroke: "#ccc" }}
                            angle={-45}
                            textAnchor="end"
                          />
                          <YAxis
                            tick={{ fontSize: 12, fill: "#0d2d54" }}
                            axisLine={{ stroke: "#ccc" }}
                            tickLine={{ stroke: "#ccc" }}
                          />
                          <Tooltip labelFormatter={(label) => label} />
                          <Line
                            type="monotone"
                            dataKey="value"
                            stroke="#0d2d54"
                            strokeWidth={2}
                            dot={{ r: 4, fill: "#0d2d54" }}
                            activeDot={{ r: 6, fill: "#32a2dc" }}
                          />
                        </LineChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : (
                <p>{t("progress_report_student_no_metrics")}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default React.memo(GroupReports);
